import { apiClient } from './apiClient';

/**
 * MV5 Wave 1 · Portal API client.
 *
 * Migrated onto `@hauraki/api-client` (`apiClient.me` / `apiClient.portal` /
 * `apiClient.payments`) — this file is now a thin adapter: every exported
 * type and every `portalApi` method keeps its original name, parameter
 * list, and resolved value shape. See `apiClient.ts` for the transport
 * (fetch + injected token provider) and 401 behavior (reproduces the axios
 * interceptor's toast + soft-redirect, no refresh-then-retry — same as
 * before).
 */

export type CountryCode = 'NZ' | 'AU' | 'PK' | 'IN' | 'SA' | 'GB' | 'CA' | 'US' | 'OTHER';

export interface EnrolledVertical {
  slug: string;
  name: string;
  founding_member_number: number | null;
  status: 'active' | 'paused' | 'graduated' | 'cancelled';
  enrolled_at: string;
}

export interface CohortMembership {
  cohort_id: string;
  vertical_slug: string;
  started_at: string;
}

export interface UserContext {
  user_id: string;
  email: string;
  role: string;
  onboarding_complete: boolean;
  country_code: CountryCode;
  content_source_slug: string;
  currency: string;
  timezone: string;
  default_language: string;
  rtl_flag: boolean;
  legal_regime: string | null;
  enrolled_verticals: EnrolledVertical[];
  cohort_memberships: CohortMembership[];
  preferred_locale: string;
  /** Requested locale and deployment-resolved learner delivery language. */
  requested_content_language?: ContentLanguage;
  resolved_content_language?: ContentLanguage;
  available_content_languages?: ContentLanguage[];
  content_language_fallback?: boolean;
  content_rtl?: boolean;
}

export interface EnrollmentSummary {
  vertical_slug: string;
  vertical_name: string;
  status: string;
  founding_member_number: number | null;
  enrolled_at: string;
  current_cert_slug: string | null;
  current_cert_name: string | null;
  overall_progress_pct: number;
  next_item_title: string | null;
  next_item_href: string | null;
  next_item_id: string | null;
  next_item_type: CourseDetailItemType | null;
}

export interface CourseTesterCourse {
  id: string;
  slug: string;
  title: string;
  expiresAt: string;
  /** Canonical course shell; it selects the real server-owned resume item. */
  href: string;
}

export interface CourseTesterAccess {
  environment: string;
  courses: CourseTesterCourse[];
  hasCourseTesterGrant: boolean;
}

/**
 * Migration 040 · core/elective ladder model. A cert is either part of the
 * required linear CORE spine or a choose-your-path ELECTIVE. Verticals that
 * haven't adopted the model report every cert as `core` with a zeroed
 * `track_summary` (no electives) — the ladder then renders exactly as before.
 */
export type TrackRole = 'core' | 'elective';

/**
 * Pick-rule completion summary for a cert whose `completion_rule` is
 * `{kind:'pick_courses', n}` (Domain AI Specializations — pick any 1 of N).
 * `null` on every all-courses cert. `required`/`completed` are course counts.
 */
export interface CertCompletion {
  kind: string;
  required: number;
  completed: number;
}

export interface CertLadderEntry {
  slug: string;
  name: string;
  order: number;
  is_current: boolean;
  is_locked: boolean;
  progress_pct: number;
  // Migration 040 core/elective fields. `track_role` groups the ladder;
  // `is_unlocked`/`is_complete` are the gating truth (locked certs must not
  // linkify). `completion` is populated only for pick-rule certs.
  track_role: TrackRole;
  is_unlocked: boolean;
  is_complete: boolean;
  completion: CertCompletion | null;
}

/**
 * Vertical-wide track-credential rollup (migration 040 `credential_rule`).
 * Always present on the payload; a vertical without the model reports zeros
 * and no required electives, which the UI reads as "legacy flat ladder".
 */
export interface TrackSummary {
  core_total: number;
  core_completed: number;
  electives_required: number;
  electives_completed: number;
  is_track_complete: boolean;
}

export interface VerticalDetail {
  slug: string;
  name: string;
  status: string;
  founding_member_number: number | null;
  cert_ladder: CertLadderEntry[];
  track_summary: TrackSummary;
}

export interface CohortMember {
  display_name: string;
  city: string | null;
  country_code: string | null;
  emphasis: string | null;
  is_self: boolean;
}

export interface CohortSummary {
  cohort_id: string | null;
  vertical_slug: string;
  vertical_name: string;
  member_count: number;
  members: CohortMember[];
}

/**
 * MV5 · Portal · cohort stats. Backs GET /portal/cohort/stats — lightweight
 * peer-context numbers for the ~47-person founding cohort (Home + cert cards).
 * `has_cohort` is false when the learner isn't in a cohort for the vertical, in
 * which case the surfaces render nothing rather than fabricate a comparison.
 */
export interface CohortStats {
  has_cohort: boolean;
  cohort_size: number;
  cohort_avg_progress_pct: number;
  your_progress_pct: number;
}

/**
 * MV5 · Portal · issued certificate for a cert. Backs
 * GET /portal/certs/:certSlug/credential. `available` is false until the cert is
 * fully completed and the credential issued — the S3 completion moment shows a
 * "being prepared" state in that case rather than a broken download.
 */
export interface CertCredential {
  available: boolean;
  status: 'available' | 'pending' | 'revoked';
  credential_id: string | null;
  cert_title: string;
  issued_at: string | null;
  share_url: string | null;
  download_url: string | null;
}

/**
 * MV5 Sprint 2.D — GET/PATCH /users/me/preferences shape. Mirrors
 * session-manager's UserPreferences.
 */
export type ViewMode = 'guided' | 'standard' | 'studio';
/**
 * Migration 031 — the learner's preferred learning-mode shell. Uses the same
 * closed set as ViewMode; kept as a separate alias so intent stays obvious at
 * call sites. Backed by student_profiles.shell_preference.
 */
/**
 * Learning-mode preference. Canonical values are `'guided'` and `'pro'`
 * (two-surface model — see docs/design-context/shell-two-surface-redesign.md).
 * `'standard'` and `'studio'` are DEPRECATED legacy values kept only so that
 * un-migrated `student_profiles.shell_preference` rows still resolve; both map
 * to `'pro'` at the shell router. Remove once the DB migration has run.
 */
export type ShellPreference = 'guided' | 'pro' | 'standard' | 'studio';
export interface UserPreferencesPayload {
  region: string;
  preferredLocale: string;
  scaffoldingPreference: 'auto' | 'high' | 'medium' | 'low';
  viewMode: ViewMode;
  firstStudioAt: string | null;
  shellPreference: ShellPreference;
}

/**
 * Content-language pilot — the language a learner reads their coursebooks and
 * chapter readings in, persisted as `preferredLocale` on the preferences
 * payload above. English is complete; Urdu/Punjabi/Pashto are rolling out
 * gradually, so anything not yet translated falls back to English at the
 * reader. This closed set is the switcher's contract with the reader resolver
 * (regions/<region>/<language>/<chapterId>.tex).
 */
export type ContentLanguage = 'en' | 'ur' | 'pa' | 'ps';

/**
 * PATCH /users/me/region response shape — every column of the matched
 * `regions` row, camelCased. Mirrors what GET /users/me/context already
 * surfaces for `country_code` so the Settings page can update its display
 * from this response alone, with no second round-trip.
 */
export interface RegionInfo {
  countryCode: string;
  name: string;
  currency: string;
  taxRate: number | null;
  taxLabel: string | null;
  rtlFlag: boolean;
  defaultLanguage: string;
  timezone: string;
  contentSourceSlug: string;
  legalRegime: string | null;
}

/**
 * MV5 Phase 3B · Portal · Course Detail payload.
 *
 * Composite read model backing the /portal/course/:slug page. When the
 * course is present in the DB the payload is derived from course_items;
 * when it's not, the endpoint returns a scaffold from the known
 * training-content courses (marked `preview: true` so the UI can badge it).
 */
export type LessonStatus = 'done' | 'current' | 'unstarted';

export interface CourseDetailCourse {
  slug: string;
  title: string;
  order: number;
  cert_slug: string | null;
  current_lesson_slug: string | null;
  /** Present on preview scaffolds; e.g. "Marketing / Course 1 · Growth Math". */
  module_label?: string;
}

/**
 * Lesson-type discriminator. `'lecture'` (the default / absent on older
 * payloads) renders the video LectureSurface; `'reading'` renders the in-app
 * textbook ReadingSurface (the single first-class "Coursebook" entry a course
 * with a book emits). The shells switch their centre surface on this.
 */
export type LessonType = 'lecture' | 'reading';

export interface CourseDetailLesson {
  slug: string;
  title: string;
  order: number;
  status: LessonStatus;
  duration_sec: number;
  /**
   * Lesson kind. Absent on older payloads → treat as `'lecture'`. A `'reading'`
   * lesson carries `reading` (the chapter locator) and no `video_url`.
   */
  type?: LessonType;
  /**
   * Reading-lesson locator (present iff `type === 'reading'`). `course_ref` is
   * the identifier the reader passes to lab-manager's chapter endpoints (the
   * course slug resolves); `chapter_ref` optionally pre-selects a chapter.
   */
  reading?: { course_ref: string; chapter_ref?: string };
  /**
   * Per-lesson video source. `null` until the lesson's media is ingested /
   * rendered — the shells treat null as "Preview mode" (LectureSurface shows
   * its play-button placeholder rather than a broken <video>).
   */
  video_url: string | null;
  /**
   * True when this lesson belongs to a unit that carries a runnable lab; false
   * for standalone lectures (e.g. course intros). Drives STUDIO's per-lesson
   * bias lock — a lesson with `has_lab === false` forces the Lecture bias and
   * greys the Balanced/IDE chips. Optional: absent on older payloads, in which
   * case the shell falls back to the course-level `has_lab` flag.
   */
  has_lab?: boolean;
  /**
   * Persisted playback position in seconds (`video_progress.last_position_sec`).
   * Lets the shell resume the lecture where the learner left off instead of
   * restarting at 0:00. Optional: absent on older payloads → treat as 0.
   */
  last_position_sec?: number;
}

export interface CourseDetailCurrentLesson {
  slug: string;
  title: string;
  /** Null while the lecture media is still being prepared. */
  video_url: string | null;
  duration_sec: number;
  watched_pct: number;
  /** Persisted resume position in seconds. Optional on older payloads. */
  last_position_sec?: number;
}

export interface CourseDetailLab {
  slug: string;
  title: string;
  status: string;
  /**
   * The lab's real UUID in lab-manager, or `null` for a preview scaffold that
   * has no runnable lab behind it yet. The shells key the whole run/submit/
   * instructions pipeline off THIS (not `slug`) — a null value gates the
   * editor into a "being prepared" state.
   */
  lab_id: string | null;
  delivery?: Record<string, unknown> | null;
}

export interface CourseDetailQuiz {
  /** course_items.id when the DB has the row; null for a preview scaffold. */
  id: string | null;
  slug: string;
  title: string;
  question_count: number;
  status: 'available' | 'locked' | 'passed' | 'preview' | 'preparing';
  /**
   * Explicit server capability flag. Older payloads omit it; a real id and an
   * available/passed status remain the compatibility signal in that case.
   */
  submittable?: boolean;
  /**
   * Global `course_items.position` — the same monotonic sequence lessons expose
   * as `order`. Lets the TOC interleave each quiz inline after the lesson it
   * follows. Optional: absent on older payloads → the quiz falls to the tail.
   */
  position?: number;
  /** Unit the quiz belongs to, when the item carries one. */
  module?: string | null;
}

export interface CourseDetailResource {
  title: string;
  url: string;
}

export type CourseDetailItemType = 'lecture' | 'reading' | 'lab' | 'assessment';
export type CourseDetailItemState =
  | 'not_started'
  | 'in_progress'
  | 'completed';
export type CourseDetailItemAvailability = 'available' | 'locked' | 'preparing';

/**
 * Canonical ordered learner item returned by the additive portal course graph.
 * Type-specific fields remain optional so preview/partially-ingested content can
 * be represented honestly without manufacturing a runnable target.
 */
export interface CourseDetailItem {
  id: string;
  type: CourseDetailItemType;
  module: string | null;
  position: number;
  title: string;
  duration_sec: number | null;
  state: CourseDetailItemState;
  availability: CourseDetailItemAvailability;
  counts_toward_completion: boolean;
  progress_pct: number | null;
  href: string | null;
  related_lab_item_ids: string[];
  primary_lab_item_id: string | null;
  lecture?: {
    lesson_slug: string;
    video_external_id: string | null;
    video_url: string | null;
    last_position_sec: number;
  };
  reading?: {
    course_book_slug: string;
    chapter_id: string;
  };
  lab?: {
    lab_id: string | null;
    lab_slug: string | null;
    delivery?: Record<string, unknown> | null;
  };
  assessment?: {
    question_count: number;
    attempt_count: number;
    best_score_pct: number | null;
    passed: boolean;
  };
}

export interface CourseDetailProgress {
  total_items: number;
  completable_items: number;
  completed_items: number;
  percentage: number;
  state: 'not_started' | 'in_progress' | 'completed';
  current_item_id: string | null;
  next_item_id: string | null;
}

export interface CourseDetailPayload {
  course: CourseDetailCourse;
  current_lesson: CourseDetailCurrentLesson | null;
  lessons: CourseDetailLesson[];
  /** Canonical mixed lecture/reading/assessment/lab sequence. */
  items?: CourseDetailItem[];
  /** Server-derived completion rollup over the same item sequence. */
  progress?: CourseDetailProgress;
  tabs: {
    labs: CourseDetailLab[];
    quizzes: CourseDetailQuiz[];
    discussion_count: number;
    resources: CourseDetailResource[];
  };
  textbook_chapter_md: string;
  /**
   * Server-derived convenience: `tabs.labs.length > 0`. Present on both DB
   * and preview scaffold responses. Consumed by the Course Detail page to
   * decide whether to render the Labs tab / CTA without traversing tabs.
   */
  has_lab: boolean;
  preview: boolean;
}

/**
 * MV5 Phase 3A · Portal · Cert Overview payload.
 *
 * Backs GET /portal/certs/:certSlug (routes/portal/certs.ts). Progress % on
 * courses comes from lab_progress; the `status` derives from that plus prereq
 * satisfaction.
 */
export type CertCourseStatus = 'not_started' | 'in_progress' | 'completed' | 'locked';

export interface CertOverviewCourse {
  slug: string;
  title: string;
  order: number;
  progress_pct: number;
  status: CertCourseStatus;
  prereq_slugs: string[];
  // Migration 040 · optional course inside a cert (e.g. DSPy). Never blocks
  // cert completion; the UI badges it "Optional".
  is_optional: boolean;
}

export interface CertOverviewCert {
  slug: string;
  title: string;
  short_description: string;
  progress_pct: number;
  founding_member_number: number | null;
  // Migration 040 core/elective fields (see CertLadderEntry). `completion` is
  // the pick-rule summary for Domain-style certs, `null` otherwise.
  track_role: TrackRole;
  is_unlocked: boolean;
  is_complete: boolean;
  completion: CertCompletion | null;
}

// Per-country price for a cert's vertical, resolved by the logged-in learner's
// account country (student_profiles.country_code) — the app-layer half of the
// region architecture. `price` is in `currency` (no runtime FX); apply
// `founding_discount_pct` (0-1) for the founding-cohort price. `null` when the
// vertical × country has no pricing row seeded yet.
export interface CertPricing {
  price: number;
  founding_discount_pct: number;
  currency: string;
  tax_rate: number | null;
  tax_label: string | null;
}

export interface CertOverviewPayload {
  cert: CertOverviewCert;
  courses: CertOverviewCourse[];
  capstone: { title: string; unlocked: boolean } | null;
  // The learner's account country (falls back to 'OTHER' when unset).
  country: CountryCode;
  // Country-appropriate price for this cert's vertical, or null if unseeded.
  pricing: CertPricing | null;
}

/**
 * MV5 Phase 5 · Portal · Grades payload.
 *
 * Backs GET /portal/grades. GPA is a 0-4 scale mean over graded lab_progress
 * rows; `contribution` on each rubric row is 0-1 (raw score / 100). Pending
 * components arrive with `your_score: "—"` and `contribution: 0` — the UI
 * renders those as an empty bar, not a fake number.
 */
export interface GradesStat {
  value: string;
  label: string;
}

export type GradesEvidenceConfidence =
  | 'unavailable'
  | 'provisional'
  | 'confident';

export interface GradesEvidenceCount {
  status: GradesEvidenceConfidence;
  evidence_count: number;
  confident_at: number;
}

export interface GradesRubricRow {
  component: string;
  weight_pct: number;
  your_score: string;
  class_median: string;
  contribution: number;
  your_evidence: GradesEvidenceCount;
  class_evidence: GradesEvidenceCount;
}

export interface GradesRubric {
  cert_slug: string | null;
  cert_title: string;
  rows: GradesRubricRow[];
}

export interface GradesFeedbackEntry {
  title: string;
  score: string;
  context: string;
  quote: string;
  attribution: string;
  graded_on: string;
}

export interface GradesLabSubmission {
  submission_id: string;
  lab_id: string;
  title: string;
  cert_slug: string | null;
  cert_title: string | null;
  status: string;
  score_pct: number | null;
  score_label: string;
  submitted_at: string | null;
  graded_at: string | null;
  instructor_feedback: string | null;
  tests: {
    status: 'available' | 'unavailable';
    passed: number | null;
    failed: number | null;
    total: number | null;
  };
}

export interface GradesPayload {
  scope?: { kind: 'course'; course_id: string; course_slug: string; course_title: string };
  quiz_attempts?: Array<{ attempt_id: string; assessment_id: string; title: string; score_pct: number | null; passed: boolean; submitted_at: string | null }>;
  gpa: number;
  gpa_context: string;
  stats: GradesStat[];
  confidence: {
    gpa: GradesEvidenceCount;
    rubric: Record<GradesEvidenceConfidence, number>;
  };
  certs_available: Array<{ slug: string; title: string }>;
  rubric: GradesRubric;
  feedback: GradesFeedbackEntry[];
  lab_submissions: GradesLabSubmission[];
}

export interface PortfolioCheckpoint {
  id: string;
  commit_sha: string;
  tree_sha: string;
  run_attempt_id: string;
  run_status: 'passed';
  reflection: string;
  decision_record: Record<string, unknown>;
  git_verified_at: string;
  created_at: string;
  run_completed_at: string;
  workspace_evidence: {
    aggregate_hash: string | null;
    snapshot_count: number;
  };
}

export interface PortfolioProject {
  id: string;
  lab: { id: string; title: string };
  course_item: { id: string; title: string };
  course: { slug: string; title: string };
  artifact_release_id: string;
  visibility: 'private';
  starter: {
    commit_sha: string;
    tree_sha: string;
    authorship: 'hauraki_system';
  };
  created_at: string;
  updated_at: string;
  checkpoints: PortfolioCheckpoint[];
}

export interface PortfolioPayload {
  status: 'available' | 'empty';
  visibility: 'private';
  capabilities: {
    github_export: false;
    public_sharing: false;
  };
  projects: PortfolioProject[];
}

export interface AgendaItem {
  lab_title: string;
  course_slug: string;
  course_title: string;
  cert_name: string | null;
  due_date: string; // ISO
  status: 'completed' | 'overdue' | 'upcoming';
  href: string;
}

export interface AgendaPayload {
  has_cohort: boolean;
  items: AgendaItem[];
}

/**
 * MV5 · Portal · per-lesson media. Backs
 * GET /portal/courses/:courseSlug/lessons/:lessonSlug/media.
 *
 * `chapters` and `transcript` are `[]` until an ingestion job runs — callers
 * MUST treat empty arrays as "hide the panel", never fabricate rows.
 */
export interface LessonMediaChapter {
  slug: string;
  title: string;
  offset_sec: number;
}

export interface LessonMediaTranscriptRow {
  offset_sec: number;
  text: string;
}

export interface LessonMedia {
  lesson_slug: string;
  video_url: string | null;
  duration_sec: number | null;
  /** Canonical timeline used by the authenticated lesson-progress endpoint. */
  canonical_duration_sec?: number | null;
  chapters: LessonMediaChapter[];
  transcript: LessonMediaTranscriptRow[];
  /** Language requested by learner identity or the in-player override. */
  requested_language?: ContentLanguage;
  /** Language actually served after asset/readiness fallback. */
  resolved_language?: ContentLanguage;
  /** True when the requested rendition was unavailable and another was served. */
  language_fallback?: boolean;
  /** Ready, released renditions for this exact lecture. */
  available_languages?: ContentLanguage[];
}

/**
 * Portal · AI Tutor persisted turn. Backs GET/POST /portal/tutor/history
 * (session-manager routes/portal/tutor.ts, table portal_tutor_messages).
 * Persistence is best-effort on the client — a failed load/save must never
 * break the live SSE stream.
 */
export interface TutorHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

/**
 * SIGNUP-GATE (Cluster D) · payload for confirming / updating the notification
 * email on the region waitlist. The gate already upserts a `signup_waitlist`
 * row (dedupe on email) at block time using the ZewstID email + CF-detected
 * country, so this is a best-effort CONFIRM/CORRECT — never the sole capture.
 */
export interface WaitlistConfirmRequest {
  email: string;
  detected_country: string;
  region?: string | null;
  source?: string;
}

/**
 * STAFF-GEO-PREVIEW (Cluster F) · the caller's active preview grant, as
 * returned by GET /users/me/geo-preview-grant. Non-null ONLY when the caller
 * is staff (non-student role) AND holds an active, non-expired grant — the
 * server is the sole authority, so the client treats null as "no switcher".
 *
 * `allowed_regions` are content_source_slug values; `allowed_languages` are
 * content-language codes. Either array may be `['*']`, meaning "all" — the
 * switcher expands that to the known set for display. The scoping the switcher
 * shows is advisory; every request is re-checked server-side against the grant.
 */
export interface GeoPreviewGrant {
  allowed_regions: string[];
  allowed_languages: string[];
}

export const portalApi = {
  getContext: () => apiClient.me.getContext(),
  getCourseTesterAccess: () => apiClient.me.getCourseTesterAccess(),
  getCourseTutorContext: (courseId: string, courseItemId: string) =>
    apiClient.me.getCourseTutorContext(courseId, courseItemId),
  /**
   * GET /users/me/geo-preview-grant — drives the staff "View as" switcher.
   * Returns the caller's active grant scope, or null when they hold no active
   * grant / aren't staff. Best-effort: a failure (older backend without the
   * endpoint, offline) resolves to null so a normal learner sees no switcher.
   */
  getGeoPreviewGrant: () => apiClient.me.getGeoPreviewGrant(),
  /**
   * POST /waitlist — confirm/correct the email a blocked signup is reachable at.
   * Best-effort by design: the blocked-user screen calls this AFTER the gate has
   * already captured the row, so the caller treats a failure gracefully ("you're
   * already on the list") rather than surfacing a hard error. Never throws for
   * the caller's benefit? No — it DOES reject so the caller can distinguish a
   * confirmed update from a soft fallback; the UI decides the tone.
   */
  confirmWaitlist: (body: WaitlistConfirmRequest) => apiClient.me.confirmWaitlist(body),
  completeOnboarding: () => apiClient.me.completeOnboarding(),
  /** GET /portal/certs/:slug — Cert Overview page payload. */
  getCertOverview: (certSlug: string) => apiClient.portal.getCertOverview(certSlug),

  /**
   * GET /portal/grades — composite Grades page payload. Pass `certSlug` to
   * scope the rubric to a specific cert (`?cert=<slug>`); `certs_available`
   * always comes back in full so the switcher keeps every option.
   */
  getGrades: (certSlug?: string, courseSlug?: string) => apiClient.portal.getGrades(certSlug, courseSlug),
  /** GET /portal/portfolio — owner-only, private persisted evidence. */
  getPortfolio: () => apiClient.portal.getPortfolio(),
  getEnrollments: () => apiClient.me.getEnrollments(),
  getVerticalsDetail: () => apiClient.me.getVerticalsDetail(),
  getCohort: () => apiClient.me.getCohort(),
  /** GET /portal/agenda — real cohort-dated "This week" assignments. */
  getAgenda: () => apiClient.portal.getAgenda(),

  /**
   * GET /portal/cohort/stats — lightweight peer-context numbers. Best-effort:
   * callers render nothing when `has_cohort` is false or the request fails.
   */
  getCohortStats: () => apiClient.portal.getCohortStats(),

  /**
   * GET /portal/certs/:certSlug/credential — the issued certificate for a
   * completed cert (the S3 completion moment). Returns `available:false` when
   * the cert isn't complete / not yet issued.
   */
  getCertCredential: (certSlug: string) => apiClient.portal.getCertCredential(certSlug),

  /**
   * GET /portal/tutor/history — the learner's persisted tutor turns, oldest →
   * newest. Best-effort: callers wrap this so a failure (e.g. migration 032
   * not yet applied) leaves the Tutor streaming exactly as it did in Wave 1.
   */
  getTutorHistory: () => apiClient.portal.getTutorHistory(),

  /**
   * POST /portal/tutor/history — persist the turn(s) for the current learner
   * (normally a { user, assistant } pair). Fire-and-forget on the client.
   */
  saveTutorMessages: (
    messages: Array<{ id?: string; role: 'user' | 'assistant'; content: string; context?: unknown; sources?: unknown }>,
  ) => apiClient.portal.saveTutorMessages(messages),
  getPreferences: () => apiClient.me.getPreferences(),
  updatePreferences: (
    patch: Partial<
      Pick<
        UserPreferencesPayload,
        'viewMode' | 'scaffoldingPreference' | 'shellPreference' | 'preferredLocale'
      >
    >,
  ) => apiClient.me.updatePreferences(patch),

  /**
   * PATCH /users/me/preferences { preferredLocale } — set the learner's content
   * language for the coursebook/reading reader. A thin, intent-named wrapper
   * over updatePreferences so switcher call sites read clearly and can't pass an
   * off-contract locale (the argument is the closed ContentLanguage set).
   */
  setContentLanguage: (preferredLocale: ContentLanguage) => apiClient.me.setContentLanguage(preferredLocale),

  /**
   * PATCH /users/me/region — lets a signed-in learner correct their region
   * after signup (self-serve fix for a bad ZewstID/geo-IP guess). Same auth
   * as /users/me/preferences. Throws on a well-formed but unknown country
   * code (`invalid_country_code`) — never silently defaults.
   */
  updateRegion: (countryCode: CountryCode) => apiClient.me.updateRegion(countryCode),

  /** GET /portal/courses/:slug — composite Course Detail payload. */
  getCourse: (courseSlug: string) => apiClient.portal.getCourse(courseSlug),

  /**
   * GET /portal/courses/:courseSlug/lessons/:lessonSlug/media — the lesson's
   * video URL + (once ingested) chapters and transcript. Best-effort on the
   * client: a failure returns nothing and the shells fall back to the lesson's
   * own `video_url` with no transcript/chapter panels.
   */
  getLessonMedia: (courseSlug: string, lessonSlug: string, language?: ContentLanguage | null) =>
    apiClient.portal.getLessonMedia(courseSlug, lessonSlug, language),

  /**
   * POST /portal/courses/:slug/lessons/:lessonSlug/progress
   *
   * Fire-and-forget on the LecturePlayer's 10s tick. Server upserts a
   * video_progress row with GREATEST(existing, incoming) semantics — a
   * momentary connectivity blip cannot lose progress. Routed through
   * `apiClient`'s offline queue (see `apiClient.ts` / `@hauraki/api-client`'s
   * `postIdempotent`): an offline or network-failed post queues instead of
   * throwing, so this Promise resolves the same way it always did.
   */
  postLessonProgress: (
    courseSlug: string,
    lessonSlug: string,
    watchedPct: number,
    lastPositionSec?: number,
    signals?: { replays?: number; seeks?: number },
  ): Promise<void> =>
    apiClient.portal
      .postLessonProgress(courseSlug, lessonSlug, watchedPct, lastPositionSec, signals)
      .then(() => undefined),

  /**
   * POST /payments/checkout/create-session — T1 buy→learn spine. Mints a Stripe
   * Checkout Session for the TRACK the cert belongs to (the SKU is the whole
   * vertical; see migration 041). Requires an authenticated learner — the axios
   * client attaches the ZewstID bearer, so this is the point where the anonymous
   * marketing funnel becomes an identified purchase. Returns the Stripe-hosted
   * `url` the caller redirects the browser to.
   */
  createCheckoutSession: (input: {
    cert_slug: string;
    country_code: CountryCode;
    quiz_attempt_id?: string;
  }) => apiClient.payments.createCheckoutSession(input),

  /**
   * GET /payments/checkout/session/:sessionId — poll target for the post-Stripe
   * return page. Reads our own payment_intents record (not Stripe), so it flips
   * to `succeeded` only once the webhook has written the entitlement.
   */
  getCheckoutStatus: (sessionId: string) => apiClient.payments.getCheckoutStatus(sessionId),

  /**
   * POST /payments/jazzcash/checkout/create-session — PK direct wallet
   * integration. Unlike Stripe's create-session, this does NOT return a
   * ready-made hosted URL — JazzCash has no equivalent of Stripe's own
   * checkout page. Instead it returns hash-signed form `fields` the caller
   * must auto-submit (as a real HTML form POST, not fetch/XHR) to
   * `action_url`, which redirects the learner's browser to JazzCash's
   * hosted payment page.
   */
  createJazzCashTransaction: (input: { cert_slug: string; quiz_attempt_id?: string }) =>
    apiClient.payments.createJazzCashTransaction(input),

  /**
   * GET /payments/jazzcash/checkout/status/:txnRefNo — poll target for the
   * post-JazzCash return page. Same contract as getCheckoutStatus: reads our
   * own payment_intents record, flips to `succeeded` only once
   * JazzCashService.verifyReturn() has independently confirmed the hash and
   * granted the entitlement.
   */
  getJazzCashStatus: (txnRefNo: string) => apiClient.payments.getJazzCashStatus(txnRefNo),
};
